import { Box, Card, Typography } from "@mui/material";
import { useState, useCallback, useMemo } from "react";
import { OverlayVsStockEditModal } from "./OverlayVsStockEditModal";
import { overlayVsStockChartStyles } from "../../../styles/OverlayVsStockChart.styles";

const DEFAULT_CHART_LIMIT = 100;

interface OverlayVsStockChartProps {
  stockOnlyValue: number;
  stockOverlayValue: number;
  limit?: number;
  negativeLimit?: number;
  onLimitChange?: (limit: number) => void;
  onNegativeLimitChange?: (negativeLimit: number) => void;
}

const formatPercentage = (value: number): string => {
  return `${value.toFixed(2)}%`;
};

const getBarWidthPercentage = (
  value: number,
  positiveLimit: number,
  negativeLimitMagnitude: number,
): number => {
  if (value < 0) {
    if (negativeLimitMagnitude <= 0) {
      return 0;
    }

    return Math.min((Math.abs(value) / negativeLimitMagnitude) * 100, 100);
  }

  if (positiveLimit <= 0) {
    return 0;
  }

  return Math.min((value / positiveLimit) * 100, 100);
};

const getBarWidth = (percentage: number, shouldStartFromMiddle: boolean): string => {
  if (shouldStartFromMiddle) {
    return `${percentage / 2}%`;
  }

  return `${percentage}%`;
};

const getBarDirection = (value: number): "left" | "right" =>
  value < 0 ? "right" : "left";

const getBarOffset = (shouldStartFromMiddle: boolean): string | number =>
  shouldStartFromMiddle ? "50%" : 0;

export const OverlayVsStockChart = ({
  stockOnlyValue,
  stockOverlayValue,
  limit: controlledLimit,
  negativeLimit: controlledNegativeLimit,
  onLimitChange,
  onNegativeLimitChange,
}: OverlayVsStockChartProps) => {
  const [internalLimit, setInternalLimit] = useState(DEFAULT_CHART_LIMIT);
  const [internalNegativeLimit, setInternalNegativeLimit] = useState(
    -DEFAULT_CHART_LIMIT,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const limit = controlledLimit ?? internalLimit;
  const negativeLimit = controlledNegativeLimit ?? internalNegativeLimit;

  const handleClick = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleLimitChange = useCallback(
    (newLimit: number) => {
      if (onLimitChange) {
        onLimitChange(newLimit);
      } else {
        setInternalLimit(newLimit);
      }
    },
    [onLimitChange],
  );

  const handleNegativeLimitChange = useCallback(
    (newNegativeLimit: number) => {
      if (onNegativeLimitChange) {
        onNegativeLimitChange(newNegativeLimit);
      } else {
        setInternalNegativeLimit(newNegativeLimit);
      }
    },
    [onNegativeLimitChange],
  );

  const negativeLimitMagnitude = Math.abs(negativeLimit);

  const stockOnlyPercentage = useMemo(
    () => getBarWidthPercentage(stockOnlyValue, limit, negativeLimitMagnitude),
    [stockOnlyValue, limit, negativeLimitMagnitude],
  );

  const stockOverlayPercentage = useMemo(
    () =>
      getBarWidthPercentage(stockOverlayValue, limit, negativeLimitMagnitude),
    [stockOverlayValue, limit, negativeLimitMagnitude],
  );

  const hasMixedSigns =
    (stockOnlyValue < 0 && stockOverlayValue > 0) ||
    (stockOnlyValue > 0 && stockOverlayValue < 0);

  const stockOnlyFilledWidth = useMemo(
    () => getBarWidth(stockOnlyPercentage, hasMixedSigns),
    [stockOnlyPercentage, hasMixedSigns],
  );

  const stockOverlayFilledWidth = useMemo(
    () => getBarWidth(stockOverlayPercentage, hasMixedSigns),
    [stockOverlayPercentage, hasMixedSigns],
  );

  const stockOnlyBarDirection = getBarDirection(stockOnlyValue);
  const stockOverlayBarDirection = getBarDirection(stockOverlayValue);

  const rows = [
    {
      key: "stock-only",
      label: "Stock Only",
      value: stockOnlyValue,
      width: stockOnlyFilledWidth,
      direction: stockOnlyBarDirection,
      filledStyle: overlayVsStockChartStyles.barStockOnlyFilled,
      rowSx: overlayVsStockChartStyles.barRow,
    },
    {
      key: "stock-overlay",
      label: "Stock + Overlay",
      value: stockOverlayValue,
      width: stockOverlayFilledWidth,
      direction: stockOverlayBarDirection,
      filledStyle: overlayVsStockChartStyles.barStockOverlayFilled,
      rowSx: {
        ...overlayVsStockChartStyles.barRow,
        marginBottom: 0,
      },
    },
  ] as const;

  return (
    <>
      <Card
        sx={overlayVsStockChartStyles.card}
        onClick={handleClick}
        tabIndex={0}
        role="button"
        aria-label="Click to edit chart limits"
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        <Typography variant="h6" sx={overlayVsStockChartStyles.title}>
          Overlay vs Stock Only{" "}
          <Typography
            component="span"
            sx={overlayVsStockChartStyles.titleIllustrative}
          >
            (Illustrative)
          </Typography>
        </Typography>

        <Box sx={overlayVsStockChartStyles.barsContainer}>
          {rows.map((row) => (
            <Box key={row.key} sx={row.rowSx}>
              <Typography sx={overlayVsStockChartStyles.barLabel}>
                {row.label}
              </Typography>
              <Box sx={overlayVsStockChartStyles.barContainer}>
                <Box
                  sx={{
                    ...row.filledStyle,
                    width: row.width,
                    left:
                      row.direction === "left"
                        ? getBarOffset(hasMixedSigns)
                        : "auto",
                    right:
                      row.direction === "right"
                        ? getBarOffset(hasMixedSigns)
                        : "auto",
                  }}
                />
              </Box>
              <Typography sx={overlayVsStockChartStyles.barValue}>
                {formatPercentage(row.value)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Card>

      <OverlayVsStockEditModal
        isOpened={isModalOpen}
        onClose={handleCloseModal}
        limit={limit}
        negativeLimit={negativeLimit}
        onLimitChange={handleLimitChange}
        onNegativeLimitChange={handleNegativeLimitChange}
      />
    </>
  );
};
